import { Badge, Card, CardContent, LightboxGallery } from "@beweegbaas/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Page } from "#/components/page";
import { IndexBadge } from "#/modules/les.docent-gestuurd/components/field.sociaalemotioneel-resultaat";
import { getTeacherLesInformatie } from "#/modules/les.docent-gestuurd/lesson-teacher.functions";
import {
  getLeerjaarComboTranslation,
  getSociaalEmotioneelZinTypeTranslation,
} from "#/modules/util.i18n/enum-mapping";
import { buildLocaleHref } from "#/modules/util.helpers/route-href";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/lesson/$lesson/teacher/les-info",
)({
  loader: ({ params }) => getTeacherLesInformatie({ data: { lesId: params.lesson } }),
  component: LesInformatie,
});

function LesInformatie() {
  const { lesInformatie } = Route.useLoaderData();
  const { locale, schoolyear, school } = Route.useParams();

  return (
    <Page.Container className="flex flex-col gap-4">
      {lesInformatie?.beweegactiviteiten.map((beweegactiviteit) => (
        <div key={beweegactiviteit.id}>
          <div className="flex items-baseline gap-2">
            <Badge variant="blue" size="sm" className="">
              Leerjaar {getLeerjaarComboTranslation(beweegactiviteit.leerjaarCombo)}
            </Badge>
            -<h2 className="m-0 text-sm font-semibold">{beweegactiviteit.name}</h2>
          </div>
          <hr className="my-6" />
          <div
            className="flex flex-col flex-wrap gap-6 text-sm *:flex-1
                    sm:flex-row"
          >
            <div className="max-w-[300px] flex-0">
              <h3 className="text-xs uppercase">Beweegdoelen</h3>
              <ul className="flex flex-col divide-y divide-black/5">
                {beweegactiviteit.beweegdoelen?.map((beweegdoel, index) => (
                  <li
                    key={beweegdoel.id}
                    className="flex items-baseline gap-3
                              not-first:pt-2 not-last:pb-2"
                  >
                    <IndexBadge index={index} />
                    {getSociaalEmotioneelZinTypeTranslation("IkKan")} {beweegdoel.description}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xs uppercase">Voorbereiding</h3>
              {!beweegactiviteit.voorbereiding && <span>Geen voorbereiding</span>}
              <div
                className="prose prose-sm text-sm!"
                dangerouslySetInnerHTML={{
                  __html: beweegactiviteit.voorbereiding ?? "",
                }}
              />
            </div>
            <div>
              <h3 className="text-xs uppercase">Benodigd materiaal</h3>
              {!beweegactiviteit.benodigdMateriaal && <span>Geen benodigd materiaal</span>}
              <div
                className="prose prose-sm text-sm!"
                dangerouslySetInnerHTML={{
                  __html: beweegactiviteit.benodigdMateriaal ?? "",
                }}
              />
            </div>
            <div>
              <h3 className="text-xs uppercase">Aandachtspunten</h3>
              {!beweegactiviteit.aandachtspunten && <span>Geen aandachtspunten</span>}
              <div
                className="prose prose-sm text-sm!"
                dangerouslySetInnerHTML={{
                  __html: beweegactiviteit.aandachtspunten ?? "",
                }}
              />
            </div>
            <div>
              <h3>Opstelling</h3>
              {(beweegactiviteit.opstellingsfotos ?? [])?.length === 0 && (
                <span>Geen opstelling</span>
              )}
              <LightboxGallery
                images={
                  beweegactiviteit.opstellingsfotos?.map((opstellingsfoto) => ({
                    url: buildLocaleHref(`/api/${school}/resource/${opstellingsfoto.id}`, {
                      locale,
                      schoolyear,
                    }),
                    name: opstellingsfoto.fileName,
                  })) ?? []
                }
              />
            </div>
          </div>
        </div>
      ))}
      {lesInformatie?.sociaalEmotioneelThemas.map((sociaalEmotioneelThema) => (
        <Card key={sociaalEmotioneelThema.id}>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <Badge variant="blue" size="sm">
                Leerjaar {getLeerjaarComboTranslation(sociaalEmotioneelThema.leerjaarCombo)}
              </Badge>
              -<h2 className="m-0 text-sm font-semibold">{sociaalEmotioneelThema.name}</h2>
            </div>
            <hr className="my-6" />
            <div className="flex gap-6 text-sm *:flex-1">
              <div className="max-w-[300px] flex-0">
                <h3 className="text-xs uppercase">Sociaal-emotionele doelen</h3>
                <ul className="flex flex-col divide-y divide-black/5">
                  {sociaalEmotioneelThema.sociaalEmotioneelDoelen.map(
                    (sociaalEmotioneelDoel, index) => (
                      <li
                        key={sociaalEmotioneelDoel.id}
                        className="flex items-start gap-3
                              not-first:pt-2 not-last:pb-2"
                      >
                        <IndexBadge index={index} />
                        {getSociaalEmotioneelZinTypeTranslation(
                          sociaalEmotioneelThema.sociaalEmotioneelZinType,
                        )}{" "}
                        {sociaalEmotioneelDoel.description}
                      </li>
                    ),
                  )}
                </ul>
              </div>
              <div>
                <h3 className="text-xs uppercase">Toelichting</h3>
                {!sociaalEmotioneelThema.toelichting && <span>Geen toelichting</span>}
                <div
                  className="prose prose-sm text-sm!"
                  dangerouslySetInnerHTML={{
                    __html: sociaalEmotioneelThema.toelichting ?? "",
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </Page.Container>
  );
}
